import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Stack from '@mui/material/Stack';
import IconButton from "@mui/material/IconButton";
import { Gavel, Edit, Delete } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { Select, MenuItem, FormControl, InputLabel, Grid, Typography, TextField, Box, Button } from '@mui/material'; // Dodamo komponente za filtre
import { AuthContext } from "../../AuthContext"; // Uvoz AuthContexta
import { getImageUrl } from "../../utils/imageUtils";
import { generateSlug } from "../../utils/slug";
import useMediaQuery from "../../hooks/useMediaQuery";
import MobileAdCard from "./MobileCard";
import Swal from "sweetalert2";
import "./DashboardOverview.css";

// Icons mapping for stats section
const ICONS_MAP = {
  activeAds: "list",
  soldAds: "list-check",
  favoriteAds: "heart",
  views: "eye",
};

const STATS_TRANSLATIONS = {
  activeAds: "Aktivni oglasi",
  soldAds: "Prodani oglasi",
  favoriteAds: "Priljubljeni oglasi",
  views: "Ogledov oglasov danes",
};

// Helper function to determine the background color based on item status
const getStatusClass = (status, moderation_status) => {
  // Če moderacija ni odobrena, to prevlada nad vsem
  if (moderation_status === "zavrnjen") return "#D32F2F"; // Rdeča
  if (moderation_status === "v_pregledu") return "#0288D1"; // Modra
  switch (status) {
    case "izbrisano":
      return "red";
    case "na voljo":
      return "#463CE5";
    case "prodano":
      return "#FF4367";
    case "potečeno":
      return "orange";
    default:
      return "#ccc";
  }
};

const DashboardOverview = () => {

  // Hook za preverjanje medijske poizvedbe (npr. za mobilne naprave)
  // To omogoča prilagoditev UI-ja glede na velikost zaslona
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Handlers for item actions (originalne funkcije)

  const handleAppeal = async (row) => {
    const result = await Swal.fire({
      title: "Se želite pritožiti?",
      text: "Vaš oglas bo poslan adminu v ročni pregled. AI razlog: " + (row.ai_moderation_reason || "Ni naveden."),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Da, pošlji",
      cancelButtonText: "Prekliči"
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/listings/${row.id}/appeal`, {
          method: "POST",
          credentials: "include",
        });

        if (!response.ok) throw new Error("Napaka pri pošiljanju pritožbe.");

        Swal.fire("Poslano!", "Admin bo pregledal vaš oglas.", "success");
        refreshAds(); // Osveži tabelo
      } catch (error) {
        Swal.fire("Napaka", error.message, "error");
      }
    }
  };

  const handleEditClick = (row) => {
    const slug = generateSlug(row.title);
    navigate(`/uredi-oglas/${slug}/${row.id}`);
  };

  const handleDeleteClick = async (row) => {
    if (!row.id) return;

    const slug = generateSlug(row.title);

    const isSold = row.status === "sold";

    const result = await Swal.fire({
      title: `Kaj želite storiti z oglasom "${row.title}"?`,
      icon: "question",
      showDenyButton: !isSold,
      showCancelButton: true,
      confirmButtonText: 'Izbriši',
      denyButtonText: 'Označi kot prodano',
      cancelButtonText: 'Prekliči',
      customClass: {
        confirmButton: 'swal-btn-confirm',
        denyButton: 'swal-btn-deny',
        cancelButton: 'swal-btn-cancel',
      }
    });

    if (result.isConfirmed) {
      // Delete
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/oglas/${slug}/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        if (!response.ok) throw new Error("Napaka pri brisanju");

        await refreshAds();
        Swal.fire('Izbrisano!', '', 'success');
      } catch (error) {
        Swal.fire("Napaka", error.message, "error");
      }
    } else if (result.isDenied) {
      // Mark as sold
      await markAsSold([row.id], [row.title]);
    }
  };

  const { user, loading } = useContext(AuthContext); // Uporabnik iz AuthContexta
  const [items, setAds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();
  // State to hold statistics and item data
  const [stats, setStats] = useState({
    activeAds: 0,
    soldAds: 0,
    favoriteAds: 0,
    views: 0,
  });
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [mobileSelectedIds, setMobileSelectedIds] = useState([]);

  const markAsSold = async (listingIds, listingTitles) => {
    // Preverjanje, ali imamo vse potrebne podatke
    if (!listingIds || !listingTitles || listingIds.length !== listingTitles.length) {
      console.error("Podatki za označevanje oglasov so nepopolni.");
      Swal.fire("Napaka!", "Prišlo je do notranje napake.", "error");
      return;
    }

    try {
      const promises = listingIds.map((id, index) => {
        // === POPRAVEK: 'slug' generiramo ZNOTRAJ .map zanke ===
        // Za vsak ID vzamemo ustrezen naslov iz drugega seznama
        // in iz njega ustvarimo pravilen slug.
        const slug = generateSlug(listingTitles[index]);

        // Zdaj je URL pravilno sestavljen
        const statusApiUrl = `${process.env.REACT_APP_API_URL}/oglas/${slug}/${id}/status`;

        return fetch(statusApiUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status: "prodano" }),
        });
      });

      const responses = await Promise.all(promises);

      // Ta vrstica bo zdaj varna, ker bo 'responses' vseboval veljavne objekte
      const failed = responses.filter((res) => !res.ok);

      if (failed.length > 0) {
        throw new Error("Nekateri oglasi niso bili označeni kot prodani.");
      }

      // Osvežimo stanje na frontendu, da se sprememba takoj vidi
      setAds((prevAds) =>
        prevAds.map((item) =>
          listingIds.includes(item.id) ? { ...item, status: "prodano" } : item
        )
      );

      Swal.fire({
        title: "Uspešno!",
        text: "Oglasi so bili označeni kot prodani.",
        icon: "success",
      });

    } catch (error) {
      console.error("Napaka pri označevanju oglasov:", error);
      Swal.fire({
        title: "Napaka!",
        text: error.message,
        icon: "error",
      });
    }
  };

  const refreshAds = async () => {
    if (!user) return;

    try {
      const adsResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/${user.id}/dashboard-listings`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!adsResponse.ok) throw new Error("Napaka pri nalaganju oglasov.");

      const adsData = await adsResponse.json();
      setAds(Array.isArray(adsData.listings) ? adsData.listings : []);
    } catch (error) {
      console.error("Napaka pri nalaganju oglasov:", error);
      setAds([]);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return; // Preverimo, če je uporabnik prijavljen

      try {
        const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/${user.id}/stats`, {
          method: "GET",
          credentials: "include",
        });

        if (!statsResponse.ok) {
          const responseText = await statsResponse.text();
          throw new Error(`Failed to fetch stats: ${responseText}`);
        }

        const data = await statsResponse.json();
        setStats({
          activeAds: data.active_listings_count || 0,
          soldAds: data.sold_listings_count || 0,
          favoriteAds: data.favorite_listings_made_count || 0,
          views: data.total_views_on_listings || 0,
        });
      } catch (error) {
        console.error("Failed to fetch user stats:", error);
      }
    };

    if (!loading) { // Preverimo, da se podatki naložijo, ko je uporabnik prijavljen
      fetchStats();
    }
  }, [user, loading]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        console.warn("User is not logged in.");
        setAds([]);
        return;
      }

      try {
        const adsResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/${user.id}/dashboard-listings`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!adsResponse.ok) {
          const responseText = await adsResponse.text();
          throw new Error(`Error fetching items: ${adsResponse.status} - ${responseText}`);
        }

        const adsData = await adsResponse.json(); // Parsiramo JSON
        //setAds(adsData); // Nastavimo oglase
        setAds(Array.isArray(adsData.listings) ? adsData.listings : []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setAds([]);
      }
    };

    if (!loading) { // Počakamo, da se preveri prijava
      fetchData();
    }
  }, [user, loading]);

  // Handlerji za izbor vrstic
  const handleSelectionChange = (selection) => {
    setSelectedIds(selection);
  };

  const handleDelete = async (idsToDelete) => {
    // Ugotovimo, katere ID-je uporabiti. Če funkcija ni prejela argumenta, uporabi tiste iz DataGrid.
    const finalIds = idsToDelete || selectedIds;

    if (finalIds.length === 0) {
      Swal.fire("Ni izbranih oglasov!", "", "warning");
      return;
    }

    const result = await Swal.fire({
      title: `Ali ste prepričani?`,
      text: `Želite trajno izbrisati ${finalIds.length} oglas(ov)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Da, izbriši!",
      cancelButtonText: "Prekliči",
      customClass: {
        confirmButton: 'swal-btn-confirm',
        cancelButton: 'swal-btn-cancel',
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/listings`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ listing_ids: finalIds }), // Uporabimo pravilen seznam ID-jev
        });

        if (!response.ok) throw new Error("Napaka pri brisanju oglasov.");

        await refreshAds(); // Osvežimo seznam
        Swal.fire(`Izbrisano!`, `${finalIds.length} oglas(ov) je bilo uspešno izbrisanih.`, "success");

        // Počistimo izbore in izklopimo način izbire
        setSelectedIds([]);
        setMobileSelectedIds([]);
        setIsSelectMode(false);

      } catch (error) {
        Swal.fire("Napaka", error.message, "error");
      }
    }
  };

  const handleMarkAsSold = async (idsToMark) => {
    const finalIds = idsToMark || selectedIds;

    if (finalIds.length === 0) {
      Swal.fire("Ni izbranih oglasov!", "", "warning");
      return;
    }

    const selectedAds = items.filter(item => finalIds.includes(item.id));
    const unsoldAds = selectedAds.filter(item => item.status !== "prodano");

    if (unsoldAds.length === 0) {
      Swal.fire("Vsi izbrani oglasi so že prodani.", "", "info");
      return;
    }

    const unsoldIds = unsoldAds.map(item => item.id);
    const unsoldTitles = unsoldAds.map(item => item.title);

    const result = await Swal.fire({
      title: `Ste prepričani?`,
      text: `Želite označiti ${unsoldIds.length} oglas(ov) kot prodane?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Da, označi",
      cancelButtonText: "Prekliči",
      customClass: {
        confirmButton: 'swal-btn-deny',
        cancelButton: 'swal-btn-cancel',
      }
    });

    if (result.isConfirmed) {
      try {
        await markAsSold(unsoldIds, unsoldTitles); // Kličemo interno funkcijo, ki dela s seznami

        // Počistimo izbore in izklopimo način izbire
        setSelectedIds([]);
        setMobileSelectedIds([]);
        setIsSelectMode(false);

      } catch (error) {
        Swal.fire("Napaka!", error.message, "error");
      }
    }
  };

  // --- NOVO: Stanje za filtre in sortiranje ---
  const [filterStatus, setFilterStatus] = useState('vsi'); // 'vsi', 'na voljo', 'prodano', ...
  const [sortBy, setSortBy] = useState('datum_desc'); // 'datum_desc', 'cena_asc', 'cena_desc'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('vse');
  const [categories, setCategories] = useState([]); // Za shranjevanje seznama kategorij

  // --- useEffect ZA PRIDOBIVANJE KATEGORIJ ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/categories`);
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // --- NOVO: Logika za filtriranje in sortiranje oglasov ---
  const filteredAndSortedAds = React.useMemo(() => {
    let processedAds = [...items];

    // 1. Iskanje po nazivu (vedno prvo)
    if (searchTerm) {
      processedAds = processedAds.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 2. Filtriranje po statusu
    if (filterStatus !== 'vsi') {
      processedAds = processedAds.filter(item => item.status === filterStatus);
    }

    // 3. Filtriranje po kategoriji
    if (filterCategory !== 'vse') {
      processedAds = processedAds.filter(item => item.category?.id === filterCategory);
    }

    // 4. Sortiranje
    switch (sortBy) {
      case 'datum_asc':
        processedAds.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'cena_asc':
        processedAds.sort((a, b) => a.price - b.price);
        break;
      case 'cena_desc':
        processedAds.sort((a, b) => b.price - a.price);
        break;
      case 'datum_desc':
      default:
        processedAds.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    return processedAds;
  }, [items, filterStatus, sortBy, searchTerm, filterCategory]);

  // --- NOVO: Logika za mobilno izbiro oglasov ---

  // Funkcija za preklop posameznega oglasa v načinu izbire
  const handleMobileSelectToggle = (adId) => {
    setMobileSelectedIds(prev =>
      prev.includes(adId)
        ? prev.filter(id => id !== adId)
        : [...prev, adId]
    );
  };

  // Definicija stolpcev za tabelo
  const columns = [
    {
      field: "image",
      headerName: "Slika",
      width: 100, // Prilagodi širino stolpca, če je potrebno (npr. 110, da je prostor za padding)
      renderCell: (params) => {
        const { images: rowImages, title: rowTitle, id: rowId } = params.row;
        const slug = generateSlug(rowTitle);

        const primaryImage = rowImages?.find(img => img.is_primary) || rowImages?.[0];
        const imageKey = primaryImage?.image_url;
        const imageUrl = getImageUrl(imageKey, 'card', { text: "Oglas" });

        // Stili za kontejner slike
        const imageContainerStyles = {
          width: 85,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: "4px",
          cursor: 'pointer', // Dodaj cursor pointer na celoten Box, če je klikabilen
          backgroundColor: imageKey ? 'transparent' : '#f0f0f0', // Placeholder barva
          border: !imageKey ? '1px dashed #ccc' : 'none', // Placeholder obroba
        };

        // Stili za sliko
        const imageStyles = {
          display: 'block', // Odstrani morebitni prostor pod sliko
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "cover",
        };

        return (
          <Box sx={imageContainerStyles} onClick={() => navigate(`/oglas/${slug}/${rowId}`)}>
            {imageKey ? ( // Pogojno renderiraj img samo, če imamo ključ
              <img
                src={imageUrl}
                alt={rowTitle || "Slika oglasa"}
                style={imageStyles}
                loading="lazy"
              />
            ) : (
              // Lahko prikažeš ikono ali tekst, če ni slike, namesto samo sivega ozadja
              // Npr. <ImageIcon sx={{ color: '#bdbdbd' }} /> ali preprost tekst
              null // Ali pa pustiš, da se prikaže samo Box s placeholder stilom
            )}
          </Box>
        );
      },
    },
    {
      field: "title",
      headerName: "Naziv",
      width: 200,
      renderCell: (params) => {
        const slug = generateSlug(params.value);
        return (
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px", cursor: "pointer" }}
            onClick={() => navigate(`/oglas/${slug}/${params.row.id}`)}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "price",
      headerName: "Cena",
      width: 100,
      renderCell: (params) => (
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px" }}>
          {params.value} €
        </span>
      ),
    },
    {
      field: "category",
      headerName: "Kategorija",
      width: 150,
      renderCell: (params) => (
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px" }}>
          {params.value?.name}
        </span>
      ),
      //valueGetter: (params) => params.row.category, // To omogoča dostop do `category` objekta
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const { status, moderation_status } = params.row;

        // Logika za prikazano besedilo
        let displayText = status;
        if (moderation_status === 'zavrnjen') displayText = 'zavrnjen';
        if (moderation_status === 'v_pregledu') displayText = 'v pregledu';

        return (
          <span
            style={{
              display: "inline-block",
              minWidth: "90px", // Malo razširimo za daljše besede
              padding: "0 10px",
              height: "25px",
              lineHeight: "25px",
              borderRadius: "25px",
              textAlign: "center",
              color: "#ffffff",
              backgroundColor: getStatusClass(status, moderation_status),
              fontFamily: "'Roboto', sans-serif",
              fontSize: "14px",
              textTransform: "capitalize"
            }}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "Akcije",
      width: 150,
      renderCell: ({ row }) => (
        <Box>
          {row.moderation_status === 'zavrnjen' && (
            <Tooltip title="Pritožba na zavrnitev" arrow>
              <IconButton
                onClick={() => handleAppeal(row)}
                sx={{ color: "#145bb8ff" }}
              >
                <Gavel />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Uredi oglas" arrow>
            <IconButton onClick={() => handleEditClick(row)} sx={{ color: "#14B8A6" }} aria-label="uredi">
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Izbriši oglas" arrow>
            <IconButton onClick={() => handleDeleteClick(row)} sx={{ color: "red" }} aria-label="izbriši">
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <div className="dashboard-overview">
      <h2>Nadzorna plošča</h2>
      <hr className="hr_dashboard" />

      {/* Display statistics */}
      <div className="row">
        <div className="stats-cards">
          {Object.entries(stats).map(([key, value]) => (
            <div className="card-stat-modern" key={key}>
              <div className="icon-container">
                <i className={`fas fa-${ICONS_MAP[key]}`}></i>
              </div>
              <div className="card-info-modern">
                <h3>{value}</h3>
                <p>{STATS_TRANSLATIONS[key] || key}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <hr className="hr_dashboard" />

      {/* --- Gumbi za VKLOP/IZKLOP načina izbire --- */}
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={() => {
            setIsSelectMode(!isSelectMode);
            setMobileSelectedIds([]); // Počistimo izbor ob izklopu
          }}>
            {isSelectMode ? 'Prekliči' : 'Izberi oglase'}
          </Button>
        </Box>
      )}

      {/* --- NOVO: Prikaz filtrov in sortiranja --- */}
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Išči po nazivu..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-status-label">Status</InputLabel>
                <Select
                  labelId="filter-status-label"
                  value={filterStatus}
                  label="Filter po statusu"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="vsi">Vsi</MenuItem>
                  <MenuItem value="na voljo">Na voljo</MenuItem>
                  <MenuItem value="prodano">Prodano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-category-label">Kategorija</InputLabel>
                <Select
                  labelId="filter-category-label"
                  value={filterCategory}
                  label="Kategorija"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="vse">Vse kategorije</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-by-label">Razvrsti po</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  label="Razvrsti po"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="datum_desc">Najnovejši najprej</MenuItem>
                  <MenuItem value="datum_asc">Najstarejši najprej</MenuItem>
                  <MenuItem value="cena_desc">Cena (najvišja)</MenuItem>
                  <MenuItem value="cena_asc">Cena (najnižja)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Gumbi za masovne akcije - prikažejo se samo na namizju, ker tam uporabljamo tabelo z izbirnimi polji */}
      {!isMobile && selectedIds.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <button className="btn btn-danger mx-3 mark_sold_item_btn" onClick={handleMarkAsSold}>
            Označi kot prodano
          </button>
          <button className="btn btn-danger mx-3 delete_item_btn" onClick={handleDelete}>
            Izbriši
          </button>
        </Stack>
      )}

      {isMobile ? (
        // POGLED ZA MOBILNE NAPRAVE: Uporabimo novo, filtrirano in sortirano listo
        <Box>
          {filteredAndSortedAds.length > 0 ? (
            filteredAndSortedAds.map((item) => (
              <MobileAdCard
                key={item.id}
                item={item}
                onAppeal={() => handleAppeal(item)}
                onEdit={() => handleEditClick(item)}
                onDelete={() => handleDeleteClick(item)}
                getImageUrl={getImageUrl}
                getStatusClass={getStatusClass}
                navigateToAd={() => navigate(`/oglas/${generateSlug(item.title)}/${item.id}`)}
                isSelected={mobileSelectedIds.includes(item.id)}
                onSelectToggle={() => isSelectMode && handleMobileSelectToggle(item.id)}
              />
            ))
          ) : (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>
              Ni oglasov, ki bi ustrezali filtru.
            </Typography>
          )}
        </Box>
      ) : (
        // POGLED ZA NAMIZJE: Obstoječa DataGrid tabela
        <Paper sx={{ height: "auto", width: "100%" }}>
          <DataGrid
            rows={items}
            columns={columns}
            disableSelectionOnClick
            disableRowSelectionOnClick
            checkboxSelection
            onRowSelectionModelChange={handleSelectionChange}
            pageSizeOptions={[5, 10, 100]}
            getRowHeight={() => 75}
            initialState={{
              pagination: { paginationModel: { pageSize: 5 } },
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                fontFamily: "'Roboto', sans-serif",
                fontSize: "16px",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
            }}
          />
        </Paper>
      )}

      {/* --- "PLAVAJOČA" VRSTICA Z AKCIJAMI ZA MOBILNI POGLED --- */}
      {isMobile && isSelectMode && mobileSelectedIds.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-around',
            backgroundColor: 'background.paper',
            zIndex: 1000 // Da je nad vsem
          }}>
          <Button variant="contained" color="secondary" onClick={() => handleMarkAsSold(mobileSelectedIds)}>Označi kot prodano</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(mobileSelectedIds)}>Izbriši</Button>
        </Paper>
      )}

    </div>
  );
};

export default DashboardOverview;